import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
} from "@mui/material";
import {
  obtenerTodosLosAutores,
  agregarAutor,
  actualizarAutor,
  deshabilitarAutor,
} from "../../../services/AutorService";
import Loader from "../../loader";
import { FaTrashAlt, FaEdit } from "react-icons/fa";
import Swal from "sweetalert2";
import defaultAuthorPic from '../../../assets/defaultAuthor.png';

import "./autoresTable.css";

function AutoresTable() {
  const [autores, setAutores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [nuevoAutor, setNuevoAutor] = useState({
    nombreAutor: "",
    codigoCutter: "",
    numLibros: "",
    autorPic: "",
  });
  const [autorSeleccionado, setAutorSeleccionado] = useState(null);

  const fetchAutores = async () => {
    const data = await obtenerTodosLosAutores();
    setAutores(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchAutores();
  }, []);

  useEffect(() => {
    if (autorSeleccionado) {
      setNuevoAutor({
        nombreAutor: autorSeleccionado.nombreAutor,
        codigoCutter: autorSeleccionado.codigoCutter,
        autorPic: autorSeleccionado.autorPic,
      });
    }
  }, [autorSeleccionado]);

  // Función para crear un nuevo autor
  const handleCrearAutor = async () => {
    try {
      // Validar campos requeridos
      if (
        !nuevoAutor.nombreAutor.trim() ||
        !nuevoAutor.codigoCutter.trim() ||
        !nuevoAutor.autorPic.trim()
      ) {
        Swal.fire({
          title: "Advertencia!",
          text: "Por favor, completa todos los campos requeridos.",
          icon: "warning",
          confirmButtonText: "Ok",
          confirmButtonColor: "#21807C",
        });
        return;
      }

      const autorParaCrear = {
        nombreAutor: nuevoAutor.nombreAutor.trim(),
        codigoCutter: nuevoAutor.codigoCutter.trim(),
        autorPic: nuevoAutor.autorPic.trim(),
      };
      await agregarAutor(autorParaCrear);
      setDialogoAbierto(false);
      setNuevoAutor({
        nombreAutor: "",
        codigoCutter: "",
        autorPic: "",
      });
      Swal.fire({
        title: "Éxito!",
        text: "El autor se ha guardado con éxito.",
        icon: "success",
        confirmButtonText: "Ok",
        confirmButtonColor: "#21807C",
      });
      fetchAutores();
    } catch (error) {
      console.error("Error al crear el autor:", error);
      Swal.fire({
        title: "Error!",
        text: "No se pudo guardar el autor.",
        icon: "error",
        confirmButtonText: "Ok",
        confirmButtonColor: "#21807C",
      });
    }
  };

  // Función para actualizar un autor existente
  const handleActualizarAutor = async () => {
    try {
      // Validar campos requeridos
      if (
        !nuevoAutor.nombreAutor.trim() ||
        !nuevoAutor.codigoCutter.trim() ||
        !nuevoAutor.autorPic.trim()
      ) {
        Swal.fire({
          title: "Advertencia!",
          text: "Por favor, completa todos los campos requeridos.",
          icon: "warning",
          confirmButtonText: "Ok",
          confirmButtonColor: "#21807C",
        });
        return;
      }

      const autorParaActualizar = {
        nombreAutor: nuevoAutor.nombreAutor.trim(),
        codigoCutter: nuevoAutor.codigoCutter.trim(),
        autorPic: nuevoAutor.autorPic.trim(),
      };
      await actualizarAutor(autorSeleccionado.idAutor, autorParaActualizar);
      Swal.fire({
        title: "Éxito!",
        text: "El autor se ha actualizado con éxito.",
        icon: "success",
        confirmButtonText: "Ok",
        confirmButtonColor: "#21807C",
      });
      fetchAutores();
      setDialogoAbierto(false);
      setAutorSeleccionado(null);
      setNuevoAutor({
        nombreAutor: "",
        codigoCutter: "",
        autorPic: "",
      });
    } catch (error) {
      console.error("Error al actualizar el autor:", error);
      Swal.fire({
        title: "Error!",
        text: "No se pudo actualizar el autor.",
        icon: "error",
        confirmButtonText: "Ok",
        confirmButtonColor: "#21807C",
      });
    }
  };

  const handleDeshabilitarAutor = async (idAutor) => {
    try {
      await deshabilitarAutor(idAutor);
      Swal.fire({
        title: "Éxito!",
        text: "El autor ha sido deshabilitado con éxito.",
        icon: "success",
        confirmButtonText: "Ok",
        confirmButtonColor: "#21807C",
      });
      fetchAutores(); // Recargar la lista de autores
    } catch (error) {
      const mensajeError =
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
          ? error.response.data.message
          : error.response.data;
      Swal.fire({
        title: "Error!",
        text: mensajeError,
        icon: "error",
        confirmButtonText: "Ok",
        confirmButtonColor: "#21807C",
      });
    }
  };

  const abrirDialogo = (autor = null) => {
    setDialogoAbierto(true);
    if (autor) {
      setAutorSeleccionado(autor);
      setNuevoAutor({
        nombreAutor: autor.nombreAutor || "",
        codigoCutter: autor.codigoCutter || "",
        autorPic: autor.autorPic || "",
      });
    } else {
      setAutorSeleccionado(null);
      setNuevoAutor({
        nombreAutor: "",
        codigoCutter: "",
        autorPic: "",
      });
    }
  };

  const handleChange = (e) => {
    setNuevoAutor({ ...nuevoAutor, [e.target.name]: e.target.value });
  };

  const columnas = [
    {
      field: "idAutor",
      headerName: "ID",
      width: 120,
      headerClassName: "tablaInventario-headers",
      align: "center",
      headerAlign: "center",
    },
    {
      field: "autorPic",
      headerName: "Foto de Perfil",
      width: 150,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
      renderCell: (params) => (
        <img
          src={params.value || defaultAuthorPic}
          alt="Autor Foto de Perfil"
          style={{
            width: "71px",
            height: "71px",
            borderRadius: "35px",
            objectFit: "cover",
          }}
        />
      ),
    },
    {
      field: "nombreAutor",
      headerName: "Nombre",
      width: 200,
      headerClassName: "tablaInventario-headers",
      align: "center",
      headerAlign: "center",
    },
    {
      field: "codigoCutter",
      headerName: "Cutter",
      width: 350,
      headerClassName: "tablaInventario-headers",
      align: "center",
      headerAlign: "center",
    },
    {
      field: "numLibros",
      headerName: "Número de Libros",
      type: "number",
      width: 180,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
    },
    {
      field: "acciones",
      headerName: "Acciones",
      sortable: false,
      width: 160,
      headerAlign: "center",
      align: "center",
      headerClassName: "tablaInventario-headers",
      renderCell: (params) => (
        <>
          <div style={{ display: "flex", gap: "10px" }}>
            <Button
              className="tableInventario-updateBtn"
              onClick={() => abrirDialogo(params.row)}
              style={{
                width: "40px",
                height: "40px",
              }}
            >
              <FaEdit color="white" />
            </Button>
            <Button
              className="tableInventario-deleteBtn"
              onClick={() => handleDeshabilitarAutor(params.row.idAutor)}
              style={{
                width: "40px",
                height: "40px",
              }}
            >
              <FaTrashAlt color="white" />
            </Button>
          </div>
        </>
      ),
    },
  ];

  if (loading) {
    return <Loader />;
  } else {
    return (
      <div>
        <button
          className="tableAutores-crearBtn"
          onClick={() => abrirDialogo()}
        >
          Añadir Autor
        </button>
        <div className="tablaInventarioContainer">
          <DataGrid
            rows={autores}
            columns={columnas}
            pageSize={5}
            getRowId={(row) => row.idAutor}
            disableSelectionOnClick
            rowHeight={80}
          />
        </div>

        <Dialog open={dialogoAbierto} onClose={() => setDialogoAbierto(false)}>
          <DialogTitle>
            {autorSeleccionado ? "Editar Autor" : "Crear Nuevo Autor"}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="nombreAutor"
              label="Nombre del Autor"
              type="text"
              fullWidth
              variant="outlined"
              value={nuevoAutor.nombreAutor}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              name="codigoCutter"
              label="Código Cutter"
              type="text"
              fullWidth
              variant="outlined"
              value={nuevoAutor.codigoCutter}
              onChange={handleChange}
            />
            {nuevoAutor.autorPic && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <img
                  src={nuevoAutor.autorPic}
                  alt="Vista previa de la imagen"
                  style={{
                    MaxWidth: "100%",
                    marginTop: "10px",
                    height: "220px",
                    justifySelf: "center",
                  }}
                />
              </div>
            )}
            <TextField
              margin="dense"
              name="autorPic"
              label="URL de Foto de Perfil"
              type="text"
              fullWidth
              variant="outlined"
              value={nuevoAutor.autorPic}
              onChange={handleChange}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={
                autorSeleccionado ? handleActualizarAutor : handleCrearAutor
              }
              color="success"
            >
              Guardar
            </Button>
            <Button onClick={() => setDialogoAbierto(false)} color="error">
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default AutoresTable;
